/**
 * RoleRoute — Route guard that restricts access to specific WorkspaceRoles.
 *
 * Renders children if the current user role is in requiredRoles.
 * Otherwise, redirects to redirectTo (default '/') or renders a blocked state.
 *
 * Design: black+orange only, uses CSS variable keys from index.css.
 *
 * Usage:
 *   <Route path="/orchestrator" element={
 *     <RoleRoute requiredRoles={['admin']} redirectTo="/">
 *       <OrchestratorDashboard />
 *     </RoleRoute>
 *   } />
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useWorkspaceRole } from '../../hooks/useWorkspaceRole';
import type { WorkspaceRole } from '../../config/toolsManifest';
import { Shield, ArrowLeft } from 'lucide-react';

/**
 * Props for RoleRoute component.
 * ALL props declared AND destructured in component body.
 */
export interface RoleRouteProps {
  /** Roles that are permitted to access this route */
  requiredRoles: WorkspaceRole[];
  /** Where to redirect if role is not authorized (default: '/') */
  redirectTo?: string;
  /** Child elements to render if authorized */
  children: React.ReactNode;
  /** If true, shows a blocked state instead of redirecting */
  showBlockedState?: boolean;
}

/**
 * BlockedState — Design-system-compliant access denied view.
 * Uses black+orange only, CSS variable keys.
 */
function BlockedState({ onGoBack }: { onGoBack?: () => void }): React.ReactElement {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: 'var(--color-bg-base)' }}
    >
      <div
        className="max-w-md w-full mx-4 p-8 rounded-xl border"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          borderColor: 'var(--color-border)',
        }}
      >
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{
              backgroundColor: 'var(--color-needs-human-tint)',
              border: '1px solid var(--color-needs-human-tint-bd)',
            }}
          >
            <Shield
              className="w-8 h-8"
              style={{ color: 'var(--color-brand)' }}
            />
          </div>
        </div>

        {/* Title */}
        <h1
          className="text-xl font-semibold text-center mb-3"
          style={{
            fontFamily: 'DM Serif Display, Georgia, serif',
            color: 'var(--color-text-primary)',
          }}
        >
          Access Restricted
        </h1>

        {/* Description */}
        <p
          className="text-center mb-6"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          You don&apos;t have permission to access this page.
          Contact your administrator if you believe this is an error.
        </p>

        {/* Action */}
        <div className="flex justify-center">
          <button
            onClick={onGoBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-bg-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-bg-elevated)';
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * RoleRoute — Guard component that checks workspace role before rendering.
 *
 * Props (ALL declared AND destructured):
 *   - requiredRoles: WorkspaceRole[] — roles allowed to access
 *   - redirectTo: string — where to send unauthorized users (default: '/')
 *   - children: React.ReactNode — content to render if authorized
 *   - showBlockedState: boolean — if true, show UI instead of redirect
 */
export function RoleRoute({
  requiredRoles,
  redirectTo = '/',
  children,
  showBlockedState = false,
}: RoleRouteProps): React.ReactElement {
  const currentRole = useWorkspaceRole();

  // Check if current role is in required roles
  const isAuthorized =
    currentRole !== null && requiredRoles.includes(currentRole);

  if (isAuthorized) {
    return <>{children}</>;
  }

  // Not authorized — either show blocked state or redirect
  if (showBlockedState) {
    return (
      <BlockedState
        onGoBack={() => {
          window.location.href = redirectTo;
        }}
      />
    );
  }

  // Redirect to specified path
  return <Navigate to={redirectTo} replace />;
}

export default RoleRoute;
